import React, { useState, useEffect } from 'react';
import {
  Dialog, DialogContent, DialogActions, Box, Typography,
  Button, LinearProgress, Alert, Chip,
} from '@mui/material';
import {
  CheckCircle, AutoFixHigh, OpenInNew, CallMerge, ArrowForward,
} from '@mui/icons-material';

interface RemediationProgressModalProps {
  open: boolean;
  onClose: () => void;
  count: number;
  isFixing: boolean;
  isComplete: boolean;
  prUrl?: string | null;
  prNumber?: number | null;
  onReviewChanges: () => void;
}

const STAGES = [
  'Analysing findings...',
  'Generating remediation...',
  'Validating changes...',
  'Preparing pull request...',
];

const RemediationProgressModal: React.FC<RemediationProgressModalProps> = ({
  open,
  onClose,
  count,
  isFixing,
  isComplete,
  prUrl,
  prNumber,
  onReviewChanges,
}) => {
  const [stageIdx, setStageIdx] = useState(0);
  const [processedCount, setProcessedCount] = useState(1);

  useEffect(() => {
    if (!isFixing) {
      setStageIdx(0);
      setProcessedCount(count);
      return;
    }

    setStageIdx(0);
    setProcessedCount(1);

    const stageTimer = setInterval(() => {
      setStageIdx((prev) => (prev + 1) % STAGES.length);
    }, 1200);

    const countTimer = setInterval(() => {
      setProcessedCount((prev) => (prev < count ? prev + 1 : count));
    }, Math.max(300, Math.floor(3000 / Math.max(1, count))));

    return () => {
      clearInterval(stageTimer);
      clearInterval(countTimer);
    };
  }, [isFixing, count]);

  const progressPercent = isComplete ? 100 : Math.min(95, Math.round((processedCount / Math.max(1, count)) * 100));

  return (
    <Dialog open={open} onClose={isFixing ? undefined : onClose} maxWidth="xs" fullWidth PaperProps={{ sx: { borderRadius: 3, p: 1 } }}>
      <DialogContent>
        {isComplete ? (
          <Box sx={{ textAlign: 'center', py: 2 }}>
            <Box
              sx={{
                width: 56,
                height: 56,
                borderRadius: '50%',
                bgcolor: '#ecfdf5',
                color: '#10b981',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                mx: 'auto',
                mb: 2,
              }}
            >
              <CheckCircle sx={{ fontSize: 32 }} />
            </Box>

            <Typography variant="h6" fontWeight={800} color="text.primary" sx={{ mb: 0.5 }}>
              ✓ Remediation prepared
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 2.5 }}>
              {count} finding{count !== 1 ? 's' : ''} processed successfully
            </Typography>

            <Alert severity="success" sx={{ textAlign: 'left', mb: 2.5, borderRadius: 2 }}>
              A focused, reviewable remediation batch has been generated. You can inspect the modified files or view the pull request on GitHub.
            </Alert>
          </Box>
        ) : (
          <Box sx={{ py: 1 }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, mb: 2 }}>
              <AutoFixHigh sx={{ color: 'primary.main', fontSize: 24 }} />
              <Box>
                <Typography variant="h6" fontWeight={800} color="text.primary" sx={{ lineHeight: 1.2 }}>
                  Generating remediation
                </Typography>
                <Typography variant="caption" color="text.secondary">
                  Preparing fixes for {count} finding{count !== 1 ? 's' : ''}
                </Typography>
              </Box>
            </Box>

            <LinearProgress
              variant="determinate"
              value={progressPercent}
              sx={{ height: 8, borderRadius: 4, mb: 2, bgcolor: 'grey.100' }}
            />

            <Box sx={{ bgcolor: 'grey.50', p: 2, borderRadius: 2, border: '1px solid', borderColor: 'grey.200', mb: 1 }}>
              <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 1 }}>
                <Typography variant="caption" fontWeight={700} color="primary.main">
                  {STAGES[stageIdx]}
                </Typography>
                <Chip
                  label={`${processedCount} of ${count} processed`}
                  size="small"
                  color="primary"
                  sx={{ height: 20, fontSize: 10, fontWeight: 800 }}
                />
              </Box>
              <Typography variant="caption" color="text.disabled" sx={{ display: 'block' }}>
                Validating syntax, safety constraints, and building PR changes...
              </Typography>
            </Box>
          </Box>
        )}
      </DialogContent>

      <DialogActions sx={{ px: 3, pb: 2, gap: 1, justifyContent: isComplete ? 'stretch' : 'flex-end' }}>
        {isComplete ? (
          <Box sx={{ display: 'flex', gap: 1, width: '100%', flexWrap: 'wrap' }}>
            <Button
              fullWidth
              variant="contained"
              disableElevation
              color="primary"
              onClick={() => {
                onClose();
                onReviewChanges();
              }}
              startIcon={<CallMerge />}
              endIcon={<ArrowForward />}
              sx={{ textTransform: 'none', fontWeight: 800, py: 1 }}
            >
              Review changes
            </Button>
            {prUrl && (
              <Button
                fullWidth
                variant="outlined"
                color="inherit"
                onClick={() => window.open(prUrl, '_blank')}
                startIcon={<OpenInNew />}
                sx={{ textTransform: 'none', fontWeight: 700 }}
              >
                View PR #{prNumber ?? 1}
              </Button>
            )}
            <Button
              fullWidth
              variant="text"
              color="inherit"
              onClick={onClose}
              sx={{ textTransform: 'none', fontSize: 12, color: 'text.secondary' }}
            >
              Close
            </Button>
          </Box>
        ) : null}
      </DialogActions>
    </Dialog>
  );
};

export default RemediationProgressModal;
